export const redPacketHero = {
  eyebrow: '产品与解决方案 · 数字红包',
  title: '把惠民红包，发到每一位消费者手里',
  subtitle:
    '以数字人民币红包连接政策、商户与消费者，让补贴发放更精准、核销更便捷、消费促进更可见。',
  note: '适用于消费券发放、节庆营销、商圈引流、乡村振兴及员工福利等场景。',
  primaryLabel: '获取方案咨询',
  secondaryLabel: '查看应用场景',
  tags: ['数字人民币红包', '精准发放', '自动核销', '数据复盘'],
};

export interface PainPoint {
  icon: string;
  title: string;
  desc: string;
}

export const painPoints: PainPoint[] = [
  {
    icon: 'ri-crosshair-2-line',
    title: '发放不精准',
    desc: '覆盖人群与活动目标难以有效匹配，补贴容易流于撒网式投放。',
  },
  {
    icon: 'ri-exchange-line',
    title: '核销不顺畅',
    desc: '领取、使用、结算链路复杂，消费者与商户体验都受到影响。',
  },
  {
    icon: 'ri-store-2-line',
    title: '商户参与低',
    desc: '活动配置与对账成本高，商户参与门槛抬升、积极性不足。',
  },
  {
    icon: 'ri-eye-off-line',
    title: '成效看不清',
    desc: '缺少实时、可信的活动数据沉淀，投入产出难以准确衡量。',
  },
];

export interface FlowNode {
  no: string;
  icon: string;
  title: string;
  desc: string;
}

export const solutionFlow: FlowNode[] = [
  { no: '01', icon: 'ri-settings-4-line', title: '活动配置', desc: '按预算与目标配置红包规则' },
  { no: '02', icon: 'ri-send-plane-line', title: '定向发放', desc: '精准触达目标人群' },
  { no: '03', icon: 'ri-gift-2-line', title: '用户领取', desc: '领券即用，权益即时到账' },
  { no: '04', icon: 'ri-store-3-line', title: '到店消费', desc: '引导到店，激活线下消费' },
  { no: '05', icon: 'ri-check-double-line', title: '自动核销', desc: '支付即核，链路留痕' },
  { no: '06', icon: 'ri-line-chart-line', title: '数据复盘', desc: '实时掌握活动成效' },
];

export interface Capability {
  icon: string;
  title: string;
  desc: string;
  points: string[];
}

export const capabilities: Capability[] = [
  {
    icon: 'ri-settings-4-line',
    title: '灵活活动配置',
    desc: '支持固定金额、满减、随机红包、定向人群、限时领取及多轮次发放等规则。',
    points: ['固定金额', '满减 / 随机红包', '定向人群', '多轮次发放'],
  },
  {
    icon: 'ri-user-location-line',
    title: '精准人群触达',
    desc: '按地域、用户标签、活动资格等条件定向触达，让补贴真正惠及目标人群。',
    points: ['地域定向', '用户标签', '资格校验', '精准触达'],
  },
  {
    icon: 'ri-store-2-line',
    title: '便捷商户核销',
    desc: '消费者支付时自动抵扣，商户无需额外学习复杂操作，降低参与门槛。',
    points: ['支付即抵扣', '零学习成本', '低门槛参与', '对账清晰'],
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: '全程数据可视',
    desc: '实时查看发放量、领取率、核销率、带动消费及商户参与等核心指标。',
    points: ['发放量', '领取率', '核销率', '带动消费'],
  },
];

export interface Scenario {
  icon: string;
  title: string;
  desc: string;
  image: string;
}

export const scenarios: Scenario[] = [
  {
    icon: 'ri-government-line',
    title: '政府惠民促消费',
    desc: '面向区域居民发放数字红包，带动本地餐饮、零售、文旅等消费增长。',
    image:
      'https://readdy.ai/api/search-image?query=Warm%20festive%20digital%20red%20envelope%20promotion%20campaign%20illustration%20showing%20a%20vibrant%20urban%20shopping%20street%20with%20restaurant%20retail%20and%20cultural%20storefronts%20glowing%20with%20golden%20light%2C%20residents%20happily%20receiving%20digital%20red%20packets%20on%20smartphones%2C%20warm%20red%20and%20gold%20gradient%20tones%2C%20joyful%20yet%20professional%20public%20service%20atmosphere%2C%20soft%20cinematic%20glow%2C%20high%20detail%2C%20editorial%20illustration%20style&width=800&height=520&seq=redpacket-scene-gov-01&orientation=landscape',
  },
  {
    icon: 'ri-store-3-line',
    title: '商圈与街区引流',
    desc: '围绕开街、节庆、夜经济等主题活动，聚合商户资源，提升客流与转化。',
    image:
      'https://readdy.ai/api/search-image?query=Vibrant%20night%20economy%20commercial%20street%20district%20with%20festive%20red%20envelope%20decorations%20and%20glowing%20golden%20shop%20signs%2C%20crowds%20of%20shoppers%20walking%20and%20using%20smartphones%20to%20receive%20digital%20red%20packets%2C%20warm%20red%20and%20gold%20gradient%20lighting%2C%20lively%20celebratory%20atmosphere%2C%20soft%20cinematic%20glow%2C%20high%20detail%2C%20editorial%20illustration%20style&width=800&height=520&seq=redpacket-scene-street-02&orientation=landscape',
  },
  {
    icon: 'ri-plant-line',
    title: '乡村振兴与农特产品推广',
    desc: '连接农产品、合作社、乡镇商户与消费者，让消费权益更有效流向乡村产业。',
    image:
      'https://readdy.ai/api/search-image?query=Warm%20pastoral%20rural%20village%20market%20scene%20with%20fresh%20agricultural%20products%20vegetables%20and%20fruits%20displayed%20at%20cooperative%20stalls%2C%20farmers%20and%20consumers%20connecting%20through%20digital%20red%20packets%20on%20smartphones%2C%20golden%20sunlight%20over%20green%20countryside%2C%20warm%20red%20and%20gold%20accents%2C%20hopeful%20rural%20revitalization%20atmosphere%2C%20soft%20cinematic%20glow%2C%20high%20detail%2C%20editorial%20illustration%20style&width=800&height=520&seq=redpacket-scene-rural-03&orientation=landscape',
  },
  {
    icon: 'ri-gift-line',
    title: '企业福利与品牌营销',
    desc: '用于员工福利、会员激励、品牌拉新与复购活动，打造更具感知的数字化权益体验。',
    image:
      'https://readdy.ai/api/search-image?query=Modern%20corporate%20employee%20benefits%20and%20brand%20marketing%20concept%20with%20digital%20red%20envelopes%20floating%20around%20a%20group%20of%20happy%20diverse%20employees%20holding%20smartphones%2C%20warm%20red%20and%20gold%20gradient%20tones%20over%20a%20clean%20professional%20office%20background%2C%20celebratory%20yet%20sophisticated%20digital%20reward%20atmosphere%2C%20soft%20cinematic%20glow%2C%20high%20detail%2C%20editorial%20illustration%20style&width=800&height=520&seq=redpacket-scene-brand-04&orientation=landscape',
  },
];

export interface OperationStep {
  no: string;
  icon: string;
  title: string;
  desc: string;
}

export const operationFlow: OperationStep[] = [
  { no: '01', icon: 'ri-flag-2-line', title: '明确活动目标与预算', desc: '确定活动范围、人群与投入' },
  { no: '02', icon: 'ri-settings-4-line', title: '配置红包规则及适用商户', desc: '设定发放与核销规则' },
  { no: '03', icon: 'ri-send-plane-line', title: '多渠道发布与用户触达', desc: '全渠道触达目标人群' },
  { no: '04', icon: 'ri-wallet-3-line', title: '用户领取并在线下消费使用', desc: '领券即用，激活消费' },
  { no: '05', icon: 'ri-line-chart-line', title: '实时监测活动进展', desc: '动态掌握发放与核销' },
  { no: '06', icon: 'ri-file-chart-line', title: '输出复盘报告', desc: '沉淀下一轮运营策略' },
];

export interface SecurityHighlight {
  icon: string;
  title: string;
  desc: string;
}

export const securityHighlights: SecurityHighlight[] = [
  {
    icon: 'ri-file-list-3-line',
    title: '活动规则留痕',
    desc: '活动配置与规则变更全程记录，可追溯、可核验。',
  },
  {
    icon: 'ri-history-line',
    title: '核销记录追溯',
    desc: '每一笔核销记录清晰留痕，资金流向有据可查。',
  },
  {
    icon: 'ri-shield-keyhole-line',
    title: '分级权限管理',
    desc: '按角色分配运营权限，保障操作安全与数据可控。',
  },
  {
    icon: 'ri-bank-line',
    title: '资金流向清晰',
    desc: '让每一笔资金流向清晰、每一次活动可核验。',
  },
];

export interface Metric {
  icon: string;
  label: string;
  value: string;
  unit: string;
}

export const metrics: Metric[] = [
  { icon: 'ri-coins-line', label: '红包发放金额', value: '—', unit: '元' },
  { icon: 'ri-user-heart-line', label: '用户领取人数', value: '—', unit: '人' },
  { icon: 'ri-store-2-line', label: '商户参与数量', value: '—', unit: '家' },
  { icon: 'ri-percent-line', label: '红包核销率', value: '—', unit: '%' },
  { icon: 'ri-funds-line', label: '带动消费金额', value: '—', unit: '元' },
];

export interface FaqItem {
  q: string;
  a: string;
}

export const faqs: FaqItem[] = [
  {
    q: '数字红包可以提现吗？',
    a: '红包按活动规则在指定场景消费使用，具体使用范围以活动页面说明为准。',
  },
  {
    q: '商户如何参与？',
    a: '完成准入与活动配置后，商户即可加入指定活动并承接红包核销。',
  },
  {
    q: '主办方能看到哪些数据？',
    a: '可查看红包发放、领取、使用、核销、商户参与及消费带动等活动数据。',
  },
];

export const consultation = {
  eyebrow: '预约方案咨询',
  title: '让数字红包，成为消费增长的新连接',
  desc: '留下您的需求，我们将为您提供匹配区域、行业与活动目标的数字红包运营方案。',
  primaryLabel: '预约方案咨询',
  secondaryLabel: '了解合作方式',
  formTitle: '提交需求 获取专属方案',
  success: '您的需求已提交，我们将尽快与您联系，为您提供匹配的数字红包运营方案。',
};